import * as React from "react"
import { useSignal } from "@preact/signals-react"
import { Box, Divider, Typography } from "@mui/material"
import { ButtonIconSeeker, ImageSeeker, ModalSeeker, TextFieldSeeker } from "@ui-seeker/core"
import { isEmpty, postSubmit } from "@ui-seeker/utils"
import { useAppToast } from "@ui-seeker/app"

import { ActionButton, ImageSkuBlock } from "@components"
import { WEBBACK_V2_API } from "@constants"
import { productPageImageDetailsReportIssueData } from "./@signals"


export default function ProductPageImageDetailsIssueModal() {
    const { showToast } = useAppToast()
    const description = useSignal("")

    if (isEmpty(productPageImageDetailsReportIssueData.value))
        return <></>

    const { skuID, sku, ean, siteID, retailer, date } = productPageImageDetailsReportIssueData.value

    const handleClose = () => {
        productPageImageDetailsReportIssueData.value = {}
        description.value = ""
    }

    return <ModalSeeker
        hideActions
        id="product-page-image-details-issue-modal"
        isOpen={!isEmpty(productPageImageDetailsReportIssueData.value)}
        title="Report Image Issue"
        controller={handleClose}
        sx={{ width: 80 }}
    >
        <Box display="flex" flexDirection="column" justifyContent="center" alignItems="center">
            <Typography variant="subtitle1">Site</Typography>
            <Divider sx={{ bgcolor: 'primary.light', fontWeight: 'bold' }} />
            <Box display="flex" alignItems="center" sx={{ mb: 1 }}>
                <ImageSeeker
                    src={`/logos/website_ico_${siteID}.png`}
                    width={16}
                    height={16}
                    alt={retailer}
                />
                <Typography sx={{ ml: 0.5 }}>{retailer}</Typography>
            </Box>

            <Typography variant="subtitle1">SKU</Typography>
            <Divider sx={{ bgcolor: 'primary.light', fontWeight: 'bold' }} />
            <Typography sx={{ mb: 0.5 }}>{sku}</Typography>
            {ean && <Typography variant="caption" sx={{ mb: 0.5, color: 'text.secondary' }}>EAN: {ean}</Typography>}
            <ImageSkuBlock
                skuID={skuID}
                alt={sku}
                imageProps={{ sx: { height: 120 } }}
            />

            <Typography variant="subtitle1" sx={{ mt: 1 }}>Date</Typography>
            <Divider sx={{ bgcolor: 'primary.light', fontWeight: 'bold' }} />
            <Typography sx={{ mb: 1 }}>{date}</Typography>

            <TextFieldSeeker
                label="Describe the issue with the images"
                value={description.value}
                onChange={e => description.value = e.target.value}
                multiline
                rows={3}
                sx={{ width: 480, mt: 2 }}
            />

            <ActionButton
                label="Report Issue"
                icon="paper-plane"
                sx={{ mt: 3 }}
                onClick={() =>
                    showToast.promise(
                        // Close only on success: on failure the modal keeps the description so the
                        // user can retry without typing it again.
                        postSubmit(`${WEBBACK_V2_API}/product-page/image-details/report_issue`, {
                            skuID,
                            sku,
                            ean,
                            siteID,
                            retailer,
                            date,
                            description: description.value
                        })
                        .then(res => { handleClose(); return res }),
                        {
                            pending: 'Sending the issue...',
                            error: 'The issue could not be sent. Please try again in a few seconds.',
                            success: 'Issue successfully reported.',
                        }
                    )
                    // The rejection is already reported by the toast above; caught here only to
                    // avoid an unhandled rejection.
                    .catch(() => {})
                }
            />
        </Box>
    </ModalSeeker>
}
